import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { usePublicCreateAccountForm } from 'domains/public/components/create-account/utils/publicCreateAccountForm.hooks'
import { Input } from 'gin-and-tonic/common/input/Input'
import { Button } from 'gin-and-tonic/common/button/Button'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import { DashRedirect } from 'legacy/dash-components/dash-redirect/DashRedirect'
import { PublicLayout } from 'domains/public/components/layout/PublicLayout'
import { ReCaptchaCheckBox } from 'gin-and-tonic/containers/re-captcha-check-box/ReCaptchaCheckBox'

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  width: 240,
  justifyItems: 'flex-start',
})

export const PublicCreateAccount: FunctionComponent = () => {
  const {
    signUpSuccess,
    disableFormFields,
    formErrorMessageId,
    onSubmit,
    emailFieldRef,
    passwordFieldRef,
    confirmPasswordFieldRef,
    givenNameFieldRef,
    surnameFieldRef,
    phoneNumberFieldRef,
    setReCaptchaResetHandler,
    registerReCaptchaTokenField,
    setReCaptchaTokenValue,
    emailErrorMessageId,
    passwordErrorMessageId,
    confirmPasswordErrorMessageId,
    givenNameErrorMessageId,
    phoneNumberErrorMessageId,
    reCaptchaTokenErrorMessageId,
  } = usePublicCreateAccountForm()

  if (signUpSuccess) {
    return <DashRedirect push toRoute="public-pending-email-verification" />
  }

  return (
    <PublicLayout headerMessageId="public.createAccount.header">
      <form css={formStyles} onSubmit={onSubmit}>
        {/* Auth Service stores names as one fullname field. Piggy back on given name for now */}
        <Input
          name="givenName"
          disabled={disableFormFields}
          errorMessageId={givenNameErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.givenName.placeholder"
          ref={givenNameFieldRef}
          data-testid="public-create-account-form-field-given-name"
        />
        <Input
          name="surname"
          value=""
          hidden
          disabled={disableFormFields}
          placeholderMessageId="public.createAccount.form.fields.surname.placeholder"
          ref={surnameFieldRef}
          data-testid="public-create-account-form-field-surname"
        />
        <Input
          name="phoneNumber"
          disabled={disableFormFields}
          errorMessageId={phoneNumberErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.phoneNumber.placeholder"
          ref={phoneNumberFieldRef}
          data-testid="public-create-account-form-field-phone-number"
        />
        <Input
          name="email"
          disabled={disableFormFields}
          errorMessageId={emailErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.email.placeholder"
          ref={emailFieldRef}
          data-testid="public-create-account-form-field-email"
        />
        <Input
          name="password"
          type="password"
          disabled={disableFormFields}
          errorMessageId={passwordErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.password.placeholder"
          ref={passwordFieldRef}
          data-testid="public-create-account-form-field-password"
        />
        <Input
          name="confirmPassword"
          type="password"
          disabled={disableFormFields}
          errorMessageId={confirmPasswordErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.confirmPassword.placeholder"
          ref={confirmPasswordFieldRef}
          data-testid="public-create-account-form-field-confirm-password"
        />
        <ReCaptchaCheckBox
          setResetHandler={setReCaptchaResetHandler}
          action="public-create-account-form-re-captcha"
          onLoad={registerReCaptchaTokenField}
          onChange={setReCaptchaTokenValue}
          errorMessageId={reCaptchaTokenErrorMessageId}
        />
        <ErrorMessage
          collapse={false}
          messageId={formErrorMessageId}
          data-testid="public-create-account-form-error-message"
        />
        <Button
          disabled={disableFormFields}
          type="submit"
          messageId="public.createAccount.form.submit"
          data-testid="public-create-account-form-submit"
        />
      </form>
    </PublicLayout>
  )
}
